import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, MapPin, Calendar, Clock, Star, Users, AlertTriangle } from 'lucide-react';

const NAVY = '#0B1824';
const ORANGE = '#ffa133';

export default function GameSetupWizard() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const initialTeamId = routerLocation.state?.teamId || '';
  const isManual = routerLocation.state?.isManual || false; // post-game stat entry mode

  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState(() => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  });
  const [gameLocation, setGameLocation] = useState('');
  const [opponentName, setOpponentName] = useState('');
  const [isHome, setIsHome] = useState(true);

  // Tracking mode + optional roster dropdown for MY_PLAYER. The wizard
  // exposes 2 pills: Player and Team. The legacy "MY_TEAM" mode is no
  // longer creatable from the wizard but the constant still lives in
  // the codebase for any pre-existing games persisted in that mode.
  const [mode, setMode] = useState(null); // 'MY_PLAYER' | 'MY_TEAM' | 'BOTH_TEAMS' | null
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [players, setPlayers] = useState([]);

  // Active team — always sourced from nav state (no in-wizard selector)
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(initialTeamId);
  const [favoritePlayer, setFavoritePlayer] = useState(null);
  const [inProgressGame, setInProgressGame] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Fallback: if nav didn't pass a teamId (e.g. direct URL hit), pick user's last active / first team.
  useEffect(() => {
    if (initialTeamId) return;
    api.get('/teams')
      .then(r => {
        const list = r.data || [];
        setTeams(list);
        if (list.length > 0) {
          // Try last_active_team_id from current user; otherwise first team.
          api.get('/auth/me')
            .then(u => {
              const last = u.data?.last_active_team_id;
              const match = last && list.find(t => t.team_id === last);
              setSelectedTeamId(match ? last : list[0].team_id);
            })
            .catch(() => setSelectedTeamId(list[0].team_id));
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep a copy of teams for selectedTeam metadata (season_label etc.)
  useEffect(() => {
    if (!initialTeamId) return;
    api.get('/teams')
      .then(r => setTeams(r.data || []))
      .catch(() => {});
  }, [initialTeamId]);

  // Fetch favorite + in-progress + roster whenever active team changes
  useEffect(() => {
    if (!selectedTeamId) {
      setFavoritePlayer(null);
      setInProgressGame(null);
      setPlayers([]);
      return;
    }
    let cancel = false;
    api.get(`/teams/${selectedTeamId}/favorite-player`)
      .then(r => { if (!cancel) {
        const fp = r.data?.favorite_player || null;
        setFavoritePlayer(fp);
        if (fp && !selectedPlayerId) setSelectedPlayerId(fp.player_id);
      }})
      .catch(() => {});
    api.get(`/teams/${selectedTeamId}/in-progress-game`)
      .then(r => { if (!cancel) setInProgressGame(r.data?.in_progress_game || null); })
      .catch(() => {});
    api.get(`/teams/${selectedTeamId}/players`)
      .then(r => { if (!cancel) setPlayers((r.data || []).filter(p => p.active !== false)); })
      .catch(() => { if (!cancel) setPlayers([]); });
    return () => { cancel = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamId]);

  const selectedTeam = useMemo(
    () => teams.find(t => t.team_id === selectedTeamId),
    [teams, selectedTeamId]
  );

  const formValid =
    !!selectedTeamId &&
    !inProgressGame &&
    opponentName.trim() &&
    date &&
    time &&
    !!mode &&
    (mode !== 'MY_PLAYER' || !!selectedPlayerId);

  const handleSubmit = async () => {
    if (!formValid) {
      if (!selectedTeamId) {
        toast.error('No active team selected. Pick a team from the top nav.');
      } else if (inProgressGame) {
        toast.error('Resume the in-progress game before starting a new one.');
      } else if (!opponentName.trim() || !date || !time) {
        toast.error('Please fill in opponent, date, and time.');
      } else if (!mode) {
        toast.error('Pick what you want to track.');
      } else if (mode === 'MY_PLAYER' && !selectedPlayerId) {
        toast.error('Choose the player you are tracking.');
      }
      return;
    }
    setSubmitting(true);
    try {
      const dateTimeISO = new Date(`${date}T${time}`).toISOString();
      const focusPlayerId = mode === 'MY_PLAYER' ? selectedPlayerId : null;
      const awayMode = mode === 'BOTH_TEAMS' ? 'players' : 'team_only';

      // Fetch active roster so game_rosters gets populated up front.
      let homeRosterPayload = [];
      try {
        const teamPlayers = (await api.get(`/teams/${selectedTeamId}/players`)).data || [];
        const active = teamPlayers.filter(p => p.active !== false);
        const ordered = focusPlayerId
          ? [
              ...active.filter(p => p.player_id === focusPlayerId),
              ...active.filter(p => p.player_id !== focusPlayerId),
            ]
          : active;
        homeRosterPayload = ordered.map((p, idx) => ({
          player_id: p.player_id,
          is_starter: idx < 5,
          is_active: true,
        }));
      } catch { /* non-fatal */ }

      const payload = {
        team_id: selectedTeamId,
        opponent_name: opponentName.trim(),
        is_home: isHome,
        date_time: dateTimeISO,
        location: gameLocation.trim() || null,
        period_format: 'quarters',
        home_tracking_mode: 'players',
        away_tracking_mode: awayMode,
        tracking_mode: mode,
        focus_player_id: focusPlayerId,
        home_roster: homeRosterPayload,
      };
      if (selectedTeam?.season_tracking_enabled && selectedTeam?.current_season_name) {
        payload.season_label = selectedTeam.current_season_name;
      }

      const res = await api.post('/games', payload);
      const gameId = res.data.game_id;

      const navState = { mode, focusPlayerId };
      if (isManual) {
        navigate(`/manual-stats-entry/${gameId}`, { state: navState });
      } else {
        navigate(`/game/${gameId}`, { state: navState });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create game');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResume = () => {
    if (!inProgressGame) return;
    navigate(`/game/${inProgressGame.game_id}`);
  };

  return (
    <div className="max-w-xl mx-auto" data-testid="game-setup-wizard">
      {/* Header — title + inline submit pill */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-slate-100 active:scale-95 transition-all shrink-0"
          data-testid="wizard-cancel-button"
          aria-label="Cancel"
        >
          <ChevronLeft className="w-5 h-5" style={{ color: NAVY }} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold uppercase tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", color: NAVY }}>
            {isManual ? 'Manual Stats Entry' : 'New Game'}
          </h1>
          {selectedTeam && (
            <p className="text-xs uppercase tracking-[.08em] text-slate-500 font-bold truncate" data-testid="wizard-active-team-label">
              {selectedTeam.name}{selectedTeam.level ? ` · ${selectedTeam.level}` : ''}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!formValid || submitting}
          data-testid="wizard-submit-button"
          className="shrink-0 inline-flex items-center px-5 h-10 rounded-full font-bold uppercase tracking-[.08em] text-sm transition-all active:scale-[.98] disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: ORANGE, color: NAVY, fontFamily: "'Barlow Condensed', sans-serif", border: 'none' }}
        >
          {submitting ? 'Creating…' : (isManual ? 'Start Entry' : 'Start Game')}
        </button>
      </div>

      <div className="space-y-5" data-testid="wizard-step-1">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Opponent
          </Label>
          <Input
            value={opponentName}
            onChange={(e) => setOpponentName(e.target.value)}
            placeholder="e.g. North Hills Hawks"
            className="h-14 text-lg"
            autoFocus
            data-testid="wizard-opponent-input"
          />
        </div>

        <div>
          <div className="grid grid-cols-2 gap-2" data-testid="wizard-home-away-toggle">
            <button
              type="button"
              onClick={() => setIsHome(true)}
              data-testid="wizard-toggle-home"
              className="h-12 rounded-full font-bold uppercase tracking-wider text-base transition-all active:scale-[.98]"
              style={{
                background: isHome ? NAVY : '#fff',
                color: isHome ? '#fff' : NAVY,
                border: `2px solid ${NAVY}`,
                fontFamily: "'Barlow Condensed', sans-serif",
              }}
            >
              Home
            </button>
            <button
              type="button"
              onClick={() => setIsHome(false)}
              data-testid="wizard-toggle-away"
              className="h-12 rounded-full font-bold uppercase tracking-wider text-base transition-all active:scale-[.98]"
              style={{
                background: !isHome ? NAVY : '#fff',
                color: !isHome ? '#fff' : NAVY,
                border: `2px solid ${NAVY}`,
                fontFamily: "'Barlow Condensed', sans-serif",
              }}
            >
              Away
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Date
            </Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-14 text-lg"
              data-testid="wizard-date-input"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Time
            </Label>
            <Input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="h-14 text-lg"
              data-testid="wizard-time-input"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> Location <span className="text-[10px] font-normal normal-case tracking-normal text-slate-400">(optional)</span>
          </Label>
          <Input
            value={gameLocation}
            onChange={(e) => setGameLocation(e.target.value)}
            placeholder="e.g. Lincoln HS Gym A"
            className="h-14 text-lg"
            data-testid="wizard-location-input"
          />
        </div>

        {/* Tracking mode — 2 pills (required). Player → focus on a single
            roster member, away team logged at team-level. Team → both
            teams tracked at player level. The legacy 3rd "My Team" pill
            has been retired from the wizard. */}
        <div data-testid="wizard-mode-pills">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2 block">
            Who are you tracking?
          </Label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: 'MY_PLAYER',  label: 'Player' },
              { key: 'BOTH_TEAMS', label: 'Team' },
            ].map(opt => {
              const active = mode === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    setMode(opt.key);
                    if (opt.key !== 'MY_PLAYER') setSelectedPlayerId('');
                    if (opt.key === 'MY_PLAYER' && favoritePlayer && !selectedPlayerId) {
                      setSelectedPlayerId(favoritePlayer.player_id);
                    }
                  }}
                  data-testid={`wizard-mode-${opt.key.toLowerCase().replace('_','-')}`}
                  className="h-12 rounded-full text-[15px] font-semibold uppercase tracking-[.07em] transition-all active:scale-[.97]"
                  style={{
                    background: active ? ORANGE : '#fff',
                    color: NAVY,
                    border: `2px solid ${active ? ORANGE : '#cbd5e1'}`,
                    fontFamily: "'Barlow Condensed', sans-serif",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Roster dropdown — only for MY_PLAYER mode */}
        {mode === 'MY_PLAYER' && (
          <div data-testid="wizard-player-picker">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2 flex items-center gap-1.5">
              <Star className="w-3.5 h-3.5" /> Pick the player
            </Label>
            {players.length === 0 ? (
              <p className="text-xs text-slate-500 italic" data-testid="wizard-empty-roster-note">
                This team's roster is empty. Add players from the Teams page first.
              </p>
            ) : (
              <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
                <SelectTrigger className="h-14 text-base" data-testid="wizard-player-select">
                  <SelectValue placeholder="Select a player" />
                </SelectTrigger>
                <SelectContent>
                  {players.map(p => (
                    <SelectItem key={p.player_id} value={p.player_id}>
                      {p.jersey_number != null ? `#${p.jersey_number} · ` : ''}{p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        {/* In-progress game guard */}
        {inProgressGame && (
          <div
            className="rounded-xl p-4 flex items-start gap-3"
            style={{ background: 'rgba(255,161,51,0.10)', border: `1.5px solid ${ORANGE}` }}
            data-testid="wizard-in-progress-block"
          >
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" style={{ color: ORANGE }} />
            <div className="flex-1">
              <p className="text-sm font-bold" style={{ color: NAVY }}>
                Game already in progress
              </p>
              <p className="text-xs text-slate-600 mt-1">
                {selectedTeam?.name || 'This team'} has an active game vs{' '}
                <span className="font-semibold">{inProgressGame.opponent_name || 'unknown opponent'}</span>.
                Resume it before starting a new one.
              </p>
              <Button
                onClick={handleResume}
                className="mt-3 h-10 px-4 text-sm font-bold uppercase tracking-wider"
                style={{ background: NAVY, color: '#fff' }}
                data-testid="wizard-resume-button"
              >
                Resume Game
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
